import React, { useRef, useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/library-api';

import { useOverlayState } from '../../../../hooks/useOverlayState';

import { PLAYBOOK_VIEW_TYPE } from '../../../../constants/playbooks';
import { defaultErrorMessage } from '../../../../constants/errorMessages';

import Loading from '../../../../components/Loading';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import SectionContainer from '../SectionContainer/SectionContainer';
import PlaybookViewCard from './PlaybookViewCard/PlaybookViewCard';
import PlaybookViewSidePanel from './PlaybookViewSidePanel/PlaybookViewSidePanel';
import { Button } from '../../../../design-system';

const PlaybookViewsSection = ({ playbookId, playbookViewsResponse }) => {
    const {
        isOpened: isCreateViewPanelOpened,
        open: openCreateViewPanel,
        close: closeCreateViewPanel,
    } = useOverlayState();

    const {
        options: views,
        setOptions: setViews,
        optionsLoading: viewsLoading,
        canLoadMoreOptions: hasNextPage,
        setPage,
        refreshCurrentPageData,
    } = playbookViewsResponse;

    const savedDefaultViewRef = useRef(null);

    const [errorAlert, setErrorAlert] = useState(null);

    const controller = useRef(new AbortController());

    if (!views) return null;

    const setDefaultViewToState = (viewId, shouldSaveToRef = false) => {
        setViews((views) =>
            views.map((view) => {
                if (view.id === viewId) {
                    return { ...view, is_default: true };
                }
                if (view.is_default) {
                    if (shouldSaveToRef && !savedDefaultViewRef.current) {
                        savedDefaultViewRef.current = view.id;
                    }
                    return { ...view, is_default: false };
                }
                return view;
            })
        );
    };

    const makeViewDefault = async (id) => {
        controller.current?.abort();
        controller.current = new AbortController();

        try {
            setDefaultViewToState(id, true);

            const url = API.ROUTES.library.playbookView + id + '/';
            const requestBody = { is_default: true };
            const options = { signal: controller.current.signal };

            await client.patch(url, requestBody, options);

            setDefaultViewToState(id);
            savedDefaultViewRef.current = id;
        } catch (e) {
            if (e?.message === 'canceled') {
                return;
            }

            setDefaultViewToState(savedDefaultViewRef.current);
            setErrorAlert({ message: defaultErrorMessage, status: e.response?.status });
        }
    };

    const onDeleteSuccess = async ({ id, isDefault }) => {
        // if deleted view was default - set new default
        if (isDefault) {
            // set blocks playbook view as default or first not deleted playbook views
            const newDefaultView =
                views.find((view) => view.view_type === PLAYBOOK_VIEW_TYPE.blocks) ||
                views.filter((view) => view.id !== id)?.[0];
            if (newDefaultView) {
                await makeViewDefault(newDefaultView.id);
            }
        }

        refreshCurrentPageData();
    };

    const renderButton = () => (
        <button
            onClick={openCreateViewPanel}
            className="py-1.5 font-heading-bold text-[14px] font-semibold text-neutral-400 whitespace-nowrap underline underline-offset-2"
        >
            Add View
        </button>
    );

    return (
        <SectionContainer
            title="Playbook Views"
            description="Configure how clients interact with the playbook components"
            variant="secondary"
            renderButton={renderButton}
        >
            <div className="flex flex-col gap-2.5">
                {views.map((view) => (
                    <PlaybookViewCard
                        key={view.id}
                        view={view}
                        playbookId={playbookId}
                        setViews={setViews}
                        makeViewDefault={makeViewDefault}
                        onDeleteSuccess={onDeleteSuccess}
                    />
                ))}

                {hasNextPage && !viewsLoading && (
                    <Button
                        type="link"
                        size="sm"
                        text="View More"
                        onClick={() => setPage((page) => page + 1)}
                    />
                )}

                {viewsLoading && (
                    <div className="min-h-[40px]">
                        <Loading withText={false} />
                    </div>
                )}
            </div>

            {isCreateViewPanelOpened && (
                <PlaybookViewSidePanel
                    playbookId={playbookId}
                    mode="create"
                    setViews={setViews}
                    onClose={closeCreateViewPanel}
                />
            )}

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </SectionContainer>
    );
};

export default PlaybookViewsSection;
